import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, firstValueFrom } from 'rxjs';
import { GlobalsService } from './globals.service';
import { UserStateService } from './user-state.service';
import { CartItem, ProductInfo } from '../models';

// A voucher the user has tried (redeemed OR rejected). The cart persists the
// full tried list so every attempt survives a reload and is re-validated on the
// next reprice (dreambooks parity); statusKey carries the rejection reason.
export interface AppliedVoucher {
  voucherId: string;
  code: string;
  value: number;
  status?: number;
  statusKey?: string;
  minOrderValue?: number;
  error?: string | null;
  /** Precomputed view fields (redeemed vs rejected, and the rejection reason). */
  redeemed?: boolean;
  reason?: string;
}

interface PersistedCartData {
  items: CartItem[];
  vouchers: AppliedVoucher[];
  lastUpdated: number;
  /**
   * Id of the user the cart belongs to. The cart lives under a single
   * localStorage key, so without this stamp a cart added by user A leaked
   * into user B's session on the same browser. null = unknown owner
   * (pre-stamp legacy data) — treated as not-mine on the next login.
   */
  userId?: number | null;
  /**
   * The server order code of the current cart (the `code` echoed by
   * /orders/add-to-cart). The backend keys a voucher's TEMPORARY redemption
   * to this code — if a reload rebuilt the order without it, the next
   * add-to-cart would mint a fresh code, orphan that redemption, and the
   * re-submitted voucher would collide with its own held utilization
   * ("already used"). Persisted so the cart survives refreshes without
   * losing voucher continuity; cleared with the cart (place / clear /
   * owner change).
   */
  orderCode?: string | null;
}

const CART_STORAGE_KEY = 'express_cart';
export const CART_PARKED_STORAGE_KEY = 'express_cart_parked';
const CART_EXPIRY_DAYS = 7; // Cart expires after 7 days

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(GlobalsService);
  private readonly userState = inject(UserStateService);

  private readonly _items = signal<CartItem[]>([]);
  private readonly _appliedVouchers = signal<AppliedVoucher[]>([]);
  // Owner of the current cart contents (see PersistedCartData.userId).
  // Maintained by the login watcher below + the impersonation park/restore.
  private readonly _cartOwnerId = signal<number | null>(null);
  // Server order code of the current cart (see PersistedCartData.orderCode —
  // voucher tmp-redemptions are keyed to it, so it must survive reloads).
  private readonly _orderCode = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly appliedVouchers = this._appliedVouchers.asReadonly();
  readonly orderCode = this._orderCode.asReadonly();
  
  readonly totalItems = computed(() => {
    return this._items().reduce((sum, item) => sum + item.quantity, 0);
  });

  readonly totalPrice = computed(() => {
    return this._items().reduce((sum, item) => sum + (item.price * item.quantity), 0);
  });

  // Aliases for compatibility
  readonly total = this.totalPrice;
  readonly itemCount = this.totalItems;

  readonly isEmpty = computed(() => this._items().length === 0);

  private get apiUrl(): string {
    return this.globals.servicesUrl;
  }

  constructor() {
    // Load cart from localStorage on initialization
    this.loadFromStorage();

    // Auto-save to localStorage whenever items, vouchers, owner or the
    // server order code change
    effect(() => {
      const items = this._items();
      const vouchers = this._appliedVouchers();
      const ownerId = this._cartOwnerId();
      const orderCode = this._orderCode();
      this.saveToStorage(items, vouchers, ownerId, orderCode);
    });

    // Cross-user isolation: when a login lands on a cart owned by a
    // DIFFERENT user (or an unowned legacy cart with content), discard it —
    // its items/projects belong to the previous account. Logging out keeps
    // the cart, so the same user returning within the 7-day window still
    // finds it. untracked() keeps cart mutations from re-triggering this.
    effect(() => {
      const userId = this.userState.user()?.id ?? null;
      untracked(() => this.syncCartOwnership(userId));
    });
  }

  private syncCartOwnership(userId: number | null): void {
    if (userId == null) return; // logged out — leave the cart for its owner
    const ownerId = this._cartOwnerId();
    if (ownerId === userId) return;

    const hasContent = this._items().length > 0 || this._appliedVouchers().length > 0;
    if (ownerId != null || hasContent) {
      console.log('[CartService] Cart owner changed (', ownerId, '→', userId, ') — clearing persisted cart');
      this._items.set([]);
      this._appliedVouchers.set([]);
      this._orderCode.set(null);
    }
    this._cartOwnerId.set(userId);
  }

  /**
   * Load cart data from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (!stored) return;

      const data: PersistedCartData = JSON.parse(stored);
      
      // Check if cart has expired
      const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() - data.lastUpdated > expiryTime) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      // Restore items, vouchers, owner stamp and the server order code
      if (data.items?.length > 0) {
        this._items.set(data.items);
      }
      if (data.vouchers?.length > 0) {
        this._appliedVouchers.set(data.vouchers);
      }
      this._cartOwnerId.set(data.userId ?? null);
      this._orderCode.set(data.orderCode ?? null);
    } catch (error) {
      console.error('Error loading cart from storage:', error);
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }

  /**
   * Save cart data to localStorage
   */
  private saveToStorage(items: CartItem[], vouchers: AppliedVoucher[], userId: number | null, orderCode: string | null): void {
    try {
      const data: PersistedCartData = {
        items,
        vouchers,
        lastUpdated: Date.now(),
        userId,
        orderCode
      };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving cart to storage:', error);
    }
  }

  /**
   * Record the server order code the cart is currently priced under. Voucher
   * temporary redemptions are keyed to this code server-side — the cart page
   * feeds it back into rebuilt orders (e.g. after a reload) so the next
   * add-to-cart doesn't mint a fresh code and orphan those redemptions.
   */
  setOrderCode(code: string | null): void {
    if (this._orderCode() !== (code || null)) {
      this._orderCode.set(code || null);
    }
  }

  loadCart(): Observable<CartItem[]> {
    const user = this.userState.user();
    if (!user) return of([]);

    return this.http.get<CartItem[]>(`${this.apiUrl}/cart/${user.id}`).pipe(
      catchError(() => of([]))
    );
  }

  async addItem(product: ProductInfo, quantity: number = 1, options?: any): Promise<CartItem | null> {
    const user = this.userState.user();
    
    // Add to local state first
    const newItem: CartItem = {
      id: Date.now(),
      product,
      quantity,
      price: product.price || 0,
      options
    };
    this.addItemLocal(newItem);

    if (!user) return newItem;

    try {
      const serverItem = await firstValueFrom(
        this.http.post<CartItem>(`${this.apiUrl}/cart/${user.id}/add`, {
          productId: product.id,
          quantity,
          options
        })
      );
      return serverItem || newItem;
    } catch {
      return newItem;
    }
  }

  updateQuantity(itemId: number, quantity: number): void {
    const items = this._items();
    const index = items.findIndex(i => i.id === itemId);
    
    if (index >= 0) {
      const updated = [...items];
      updated[index] = { ...updated[index], quantity };
      this._items.set(updated);
    }
  }

  removeItem(itemId: number): void {
    const items = this._items().filter(i => i.id !== itemId);
    this._items.set(items);
  }

  clear(): void {
    this._items.set([]);
    this._appliedVouchers.set([]);
    this._orderCode.set(null);
    localStorage.removeItem(CART_STORAGE_KEY);
    // Note: Cart is managed locally, no server-side cart to clear
  }

  // Legacy methods for backward compatibility
  clearCart(): Observable<boolean> {
    this.clear();
    return of(true);
  }

  // Local cart management
  setItems(items: CartItem[]): void {
    this._items.set(items);
  }

  addItemLocal(item: CartItem): void {
    const items = this._items();

    // A draft can only be in the cart once. Re-adding the same draft means
    // the user reconfigured it, so the new line REPLACES the old one
    // (latest configuration/price wins; copies are the quantity field's
    // job). The options-equality check below can never catch this case:
    // options carry the server's orderCode/orderInfo, which are fresh on
    // every add-to-cart call, so two adds of one draft never compare equal.
    if (item.draftId) {
      const draftIndex = items.findIndex(i => i.draftId === item.draftId);
      if (draftIndex >= 0) {
        const updated = [...items];
        updated[draftIndex] = item;
        this._items.set(updated);
        return;
      }
    }

    const existingIndex = items.findIndex(i =>
      i.product.id === item.product.id &&
      JSON.stringify(i.options) === JSON.stringify(item.options)
    );

    if (existingIndex >= 0) {
      const updated = [...items];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + item.quantity,
        ...(item.projectThumbnail ? { projectThumbnail: item.projectThumbnail } : {}),
        ...(item.draftId ? { draftId: item.draftId } : {}),
        ...(item.draftServerName ? { draftServerName: item.draftServerName } : {})
      };
      this._items.set(updated);
    } else {
      this._items.set([...items, item]);
    }
  }

  updateItemLocal(itemId: number, quantity: number): void {
    const items = this._items();
    const index = items.findIndex(i => i.id === itemId);
    
    if (index >= 0) {
      const updated = [...items];
      updated[index] = { ...updated[index], quantity };
      this._items.set(updated);
    }
  }

  removeItemLocal(itemId: number): void {
    const items = this._items().filter(i => i.id !== itemId);
    this._items.set(items);
  }

  clearCartLocal(): void {
    this._items.set([]);
    this._orderCode.set(null);
    localStorage.removeItem(CART_STORAGE_KEY);
  }

  /**
   * Park the admin's cart before a support impersonation starts. Snapshots
   * the live signals to a separate localStorage slot, then clears in-memory
   * state so the customer session begins with an empty cart. The auto-save
   * effect will overwrite `express_cart` with the empty state on the next
   * tick, isolating any items the agent adds while impersonating.
   *
   * Why: the cart is a single LS key with no per-user scoping. Without this,
   * admin items leak into the customer session and customer-session writes
   * overwrite the admin's basket on exit.
   */
  parkForSupportSession(): void {
    try {
      const snapshot: PersistedCartData = {
        items: this._items(),
        vouchers: this._appliedVouchers(),
        lastUpdated: Date.now(),
        userId: this._cartOwnerId(),
        orderCode: this._orderCode()
      };
      localStorage.setItem(CART_PARKED_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (err) {
      console.error('[CartService] Failed to park cart for support session:', err);
      return;
    }
    this._items.set([]);
    this._appliedVouchers.set([]);
    this._orderCode.set(null);
  }

  /**
   * Restore the parked admin cart when impersonation ends. Reads from
   * localStorage (not memory) so a reload mid-impersonation — where the
   * in-memory cart belongs to the customer session — still restores the
   * admin's original basket on exit.
   */
  restoreFromSupportSession(): void {
    let snapshot: PersistedCartData | null = null;
    try {
      const raw = localStorage.getItem(CART_PARKED_STORAGE_KEY);
      if (raw) snapshot = JSON.parse(raw) as PersistedCartData;
    } catch (err) {
      console.error('[CartService] Failed to read parked cart:', err);
    }
    localStorage.removeItem(CART_PARKED_STORAGE_KEY);
    this._items.set(snapshot?.items ?? []);
    this._appliedVouchers.set(snapshot?.vouchers ?? []);
    this._orderCode.set(snapshot?.orderCode ?? null);
    // Re-stamp ownership so the login watcher doesn't discard the restored
    // cart: prefer the owner parked with the snapshot (the admin), falling
    // back to whoever is logged in now (the user swap precedes this call).
    this._cartOwnerId.set(snapshot?.userId ?? this.userState.user()?.id ?? null);
  }

  // Voucher management
  setAppliedVouchers(vouchers: AppliedVoucher[]): void {
    this._appliedVouchers.set(vouchers);
  }

  addVoucher(voucher: AppliedVoucher): void {
    const vouchers = this._appliedVouchers();
    const exists = vouchers.some(v => v.voucherId === voucher.voucherId);
    if (!exists) {
      this._appliedVouchers.set([...vouchers, voucher]);
    }
  }

  removeVoucher(voucherId: string): void {
    const vouchers = this._appliedVouchers().filter(v => v.voucherId !== voucherId);
    this._appliedVouchers.set(vouchers);
  }

  clearVouchers(): void {
    this._appliedVouchers.set([]);
  }
}
